import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useCallback, useContext } from "react";
import { useSortBy } from "react-instantsearch-core-v7";
import { DropdownInline } from "src/atoms/DropDown";
import { AppCtx } from "src/contexts/app.context";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";

const orderByKeyMap: Record<string, string> = {
  publish_date_desc: "new",
  discount_date_desc: "new-discount",
  price_desc: "price-desc",
  price_asc: "price-asc",
  popular: "popular",
} as const;

export const orderByValueMap: Record<string, string> = {
  new: "publish_date_desc",
  "new-discount": "discount_date_desc",
  "price-desc": "price_desc",
  "price-asc": "price_asc",
  popular: "",
} as const;

type SortProps = {
  handleScrollToTop: () => void;
};

function Sort({ handleScrollToTop }: SortProps): JSX.Element {
  const { locale } = useContext(AppCtx);
  const { i18n } = useLingui();
  const defaultIndexName = `${locale}_products`;

  const { refine, options, currentRefinement } = useSortBy({
    items: [
      {
        label: t(i18n)`Popularity`,
        value: defaultIndexName,
      },
      {
        label: t(i18n)`New`,
        value: `${defaultIndexName}_publish_date_desc`,
      },
      {
        label: t(i18n)`New discount`,
        value: `${defaultIndexName}_discount_date_desc`,
      },
      {
        label: t(i18n)`Price: high to low`,
        value: `${defaultIndexName}_price_desc`,
      },
      {
        label: t(i18n)`Price: low to high`,
        value: `${defaultIndexName}_price_asc`,
      },
    ],
  });

  const { handleSearchParams } = useHandleSearchParams();

  const handleChange = useCallback(
    (_event: MouseEvent, { value }: { value: string }) => {
      const valueKey = value.replace(`${defaultIndexName}_`, "");

      handleSearchParams({
        facet: "orderBy",
        value:
          orderByKeyMap[
            valueKey.length > 0 && value !== defaultIndexName
              ? valueKey
              : "popular"
          ],
        isSingle: true,
        deletePage: true,
      });

      refine(value);

      handleScrollToTop();
    },
    [defaultIndexName, refine, handleSearchParams, handleScrollToTop]
  );

  return (
    <DropdownInline
      data-testid="product-sorting-select"
      value={currentRefinement || defaultIndexName}
      options={options.map(({ label, value }) => ({ text: label, value }))}
      onChange={handleChange}
    />
  );
}

export { Sort };
